import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ApiURL from '../Api/Api';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import { SidebarContext } from './SidebarContext';
import TransactionProofImage from './TransactionProofImage';
import { formatKolkataDateTime } from '../utils/dateFormat';
import {
  ArrowPathIcon,
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  BuildingOffice2Icon,
  DocumentArrowDownIcon,
  FunnelIcon,
  ScaleIcon,
  Squares2X2Icon,
  TableCellsIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const fmt = (value) =>
  value != null
    ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(value))
    : 'INR 0';

const fmtDateTime = (value) => {
  return formatKolkataDateTime(value);
};

const txnNo = (txn) => txn.display_transaction_no || txn.transaction_no || txn.user_transaction_no || '-';

const TypeBadge = ({ type }) =>
  type === 'credit' ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded-full border border-emerald-200">
      <ArrowTrendingUpIcon className="w-2.5 h-2.5" /> Credit
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-700 text-[10px] font-bold rounded-full border border-red-200">
      <ArrowTrendingDownIcon className="w-2.5 h-2.5" /> Debit
    </span>
  );

const StatusBadge = ({ status }) => {
  const state = String(status || 'RECEIVED').toUpperCase();
  const classes = {
    SAVE: 'bg-amber-50 text-amber-700 border-amber-200',
    SAVED: 'bg-amber-50 text-amber-700 border-amber-200',
    SUBMIT: 'bg-blue-50 text-blue-700 border-blue-200',
    SUBMITTED: 'bg-blue-50 text-blue-700 border-blue-200',
    RECEIVED: 'bg-slate-50 text-slate-600 border-slate-200',
    RETURN: 'bg-orange-50 text-orange-700 border-orange-200',
    APPROVED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    'REQUEST APPROVED': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  };
  return (
    <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full border ${classes[state] || classes.RECEIVED}`}>
      {state}
    </span>
  );
};

const StatCard = ({ label, value, icon: Icon, bg, iconBg, textColor, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.3 }}
    className={`${bg} rounded-2xl p-3.5 sm:p-4 flex items-center gap-3`}
  >
    <div className={`${iconBg} w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <div className="min-w-0">
      <p className={`text-sm sm:text-base font-bold ${textColor} leading-tight`}>{value}</p>
      <p className="text-[10px] text-slate-500 mt-0.5 uppercase tracking-wider font-semibold">{label}</p>
    </div>
  </motion.div>
);

const getTravelInfo = (txn) => ({
  from: txn.from_destination || txn.from || '',
  to: txn.to_destination || txn.to || '',
  mode: txn.travel_mode || '',
});

const isTravelTransaction = (txn) => String(txn.main_head || '').trim().toLowerCase() === 'travel';

const DetailRow = ({ label, value, wide = false }) => (
  <div className={wide ? 'sm:col-span-2' : ''}>
    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">{label}</p>
    <p className="min-h-[34px] rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-[12px] font-semibold text-slate-700 break-words">
      {value || '-'}
    </p>
  </div>
);

const TransactionDetailModal = ({ txn, onClose }) => {
  if (!txn) return null;
  const travel = getTravelInfo(txn);
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center px-3 py-6"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, y: 18, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.98 }}
          transition={{ duration: 0.18 }}
          className="w-full max-w-3xl max-h-[88vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-slate-100"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="sticky top-0 bg-white/95 backdrop-blur border-b border-slate-100 px-5 py-4 flex items-center justify-between">
            <div>
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Transaction Details</p>
              <h2 className="text-base font-bold text-slate-800 mt-0.5">#{txnNo(txn)}</h2>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-xl border border-slate-200 text-slate-500 hover:text-slate-800 hover:bg-slate-50 flex items-center justify-center"
              aria-label="Close details"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>

          <div className="p-5 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <DetailRow label="Transaction No" value={txnNo(txn)} />
              <DetailRow label="Date" value={fmtDateTime(txn.transaction_date)} />
              <DetailRow label="Status" value={txn.form_status} />
              <DetailRow label="Credit" value={txn.amount_type === 'credit' ? fmt(txn.credit_amount) : '-'} />
              <DetailRow label="Debit" value={txn.amount_type === 'debit' ? fmt(txn.debit_amount) : '-'} />
              <DetailRow label="Balance" value={fmt(txn.net_balance)} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <DetailRow label="Particulars" value={txn.particuler_to} wide />
              <DetailRow label="Site" value={txn.site} />
              <DetailRow label="Project" value={txn.project_name} />
              <DetailRow label="Main Head" value={txn.main_head} />
              <DetailRow label="Sub Head" value={txn.sub_head} />
            </div>

            {(isTravelTransaction(txn) || travel.from || travel.to || travel.mode) && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <DetailRow label="Source" value={travel.from} />
                <DetailRow label="Destination" value={travel.to} />
                <DetailRow label="Travel Mode" value={travel.mode} />
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <DetailRow label="Remarks" value={txn.remarks} wide />
              <DetailRow label="Created By" value={txn.created_by} />
              <DetailRow label="Created At" value={fmtDateTime(txn.created_at || txn.original_created_date || txn.created_date)} />
            </div>

            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Proof</p>
              <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                <TransactionProofImage path={txn.attachment_path} />
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const TransactionCard = ({ txn, index, role, navigate, onView }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: Math.min(index * 0.03, 0.3) }}
    className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden active:scale-[0.99] transition-transform duration-75"
  >
    <div className={`h-0.5 w-full ${txn.amount_type === 'credit' ? 'bg-emerald-400' : 'bg-red-400'}`} />
    <div className="px-4 py-3.5">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Txn No</p>
          <p className="text-[13px] font-bold text-slate-800">#{txnNo(txn)}</p>
        </div>
        <p className={`text-base font-bold ${txn.amount_type === 'credit' ? 'text-emerald-600' : 'text-red-600'}`}>
          {txn.amount_type === 'credit' ? '+' : '-'}{fmt(txn.amount_type === 'credit' ? txn.credit_amount : txn.debit_amount)}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mb-2">
        <TypeBadge type={txn.amount_type} />
        <StatusBadge status={txn.form_status} />
        <span className="text-[11px] font-medium text-slate-400">{fmtDateTime(txn.transaction_date)}</span>
      </div>

      <p className="text-[14px] font-bold text-slate-800 leading-tight">{txn.particuler_to || '-'}</p>

      <div className="flex flex-wrap items-center justify-between gap-2 mt-2 pt-2 border-t border-slate-50">
        <div className="flex flex-wrap items-center gap-2">
          {txn.site && (
            <span className="text-[11px] text-slate-400 flex items-center gap-1">
              <BuildingOffice2Icon className="w-3 h-3" /> {txn.site}
            </span>
          )}
          {txn.main_head && (
            <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 text-[10px] font-semibold rounded-full border border-indigo-100">
              {txn.main_head}
            </span>
          )}
        </div>
        <span className="text-[11px] text-slate-400">Bal {fmt(txn.net_balance)}</span>
      </div>

      <div className="mt-2 flex items-center justify-between gap-2">
        <TransactionProofImage path={txn.attachment_path} />
        <div className="flex items-center gap-2">
          {['worker', 'admin'].includes(role) && txn.amount_type === 'credit' && (
            <button
              onClick={() => navigate(`/Return?highlight=${txn.transaction_no}`)}
              className="px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-bold"
            >
              Return
            </button>
          )}
          <button
            onClick={() => onView(txn)}
            className="px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 text-[11px] font-bold border border-indigo-100"
          >
            View
          </button>
        </div>
      </div>
    </div>
  </motion.div>
);

const WorkerTransactionReport = () => {
  const { isSidebarOpen, screenWidth } = useContext(SidebarContext);
  const navigate = useNavigate();
  const user_id = localStorage.getItem('id');
  const role = localStorage.getItem('role');

  const [transactions, setTransactions] = useState([]);
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [typeFilter, setTypeFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('');
  const [search, setSearch] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [viewMode, setViewMode] = useState(() => (typeof window !== 'undefined' && window.innerWidth < 768 ? 'cards' : 'table'));
  const [isMobile, setIsMobile] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [netBalance, setNetBalance] = useState(0);
  const [threshold, setThreshold] = useState(null);
  const [globalThreshold, setGlobalThreshold] = useState(500);
  const [requestForm, setRequestForm] = useState({
    amount: '',
    purpose: '',
    site: '',
    projectName: '',
    mainHead: '',
    subHead: '',
  });
  const [requestError, setRequestError] = useState(null);
  const [requestSuccess, setRequestSuccess] = useState(null);
  const [mainHeads, setMainHeads] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [siteOptions, setSiteOptions] = useState([]);

  const fetchTransactions = async () => {
    if (!['worker', 'admin'].includes(role) || !user_id) {
      setError('Invalid credentials or role');
      toast.error('Invalid credentials or role');
      navigate('/');
      return;
    }

    setIsLoading(true);
    try {
      const url = new URL(`${ApiURL}/getWorkerTransaction.php`);
      url.searchParams.append('user_id', user_id);
      url.searchParams.append('role', role);
      if (dateRange.startDate) url.searchParams.append('start_date', dateRange.startDate);
      if (dateRange.endDate) url.searchParams.append('end_date', dateRange.endDate);
      url.searchParams.append('t', Date.now());

      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();

      if (data.status === 'success') {
        const rows = (data.transactions || [])
          .map((txn) => ({
            ...txn,
            parsed_created_date: new Date(txn.created_at || txn.original_created_date || txn.created_date || txn.transaction_date),
          }))
          .sort((a, b) => a.parsed_created_date - b.parsed_created_date);
        setTransactions(rows);
        setNetBalance(Number(data.net_balance || 0));
        setThreshold(data.threshold);
        setGlobalThreshold(data.global_threshold || 500);
        setError(null);
      } else {
        throw new Error(data.message || 'Failed to load transactions');
      }
    } catch (err) {
      setError(`Failed to fetch transactions: ${err.message}`);
      toast.error(`Failed to fetch transactions: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFormData = async () => {
    try {
      const response = await fetch(`${ApiURL}/getTransactionForm.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: Number(user_id) }),
      });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      if (data.status === 'success') {
        const availableAttributes = Array.isArray(data.attributes) ? data.attributes : [];
        setAttributes(availableAttributes);
        setMainHeads([...new Set(availableAttributes.map((attr) => attr.main_head_name).filter(Boolean))]);
        setProjects(Array.isArray(data.projects) ? data.projects : []);
        setSiteOptions(Array.isArray(data.sites) ? data.sites : []);
      } else {
        throw new Error(data.message || 'Failed to load form data');
      }
    } catch (err) {
      setError(`Failed to fetch form data: ${err.message}`);
      toast.error(`Failed to fetch form data: ${err.message}`);
    }
  };

  const submitRequest = async (event) => {
    event.preventDefault();
    setRequestError(null);
    setRequestSuccess(null);

    if (!requestForm.amount || parseFloat(requestForm.amount) <= 0) {
      setRequestError('Amount must be greater than zero');
      toast.error('Amount must be greater than zero');
      return;
    }
    if (!requestForm.purpose.trim()) {
      setRequestError('Purpose is required');
      toast.error('Purpose is required');
      return;
    }
    try {
      const response = await fetch(`${ApiURL}/submitWorkerRequest.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          worker_id: parseInt(user_id, 10),
          role,
          amount: parseFloat(requestForm.amount),
          purpose: requestForm.purpose.trim(),
          site: requestForm.site.trim() || null,
          project_name: requestForm.projectName.trim() || null,
          main_head: requestForm.mainHead.trim() || null,
          sub_head: requestForm.subHead.trim() || null,
        }),
      });
      const data = await response.json();
      if (data.status !== 'success') throw new Error(data.message || 'Failed to submit request');

      setRequestSuccess('Request submitted successfully');
      toast.success('Request submitted successfully');
      setRequestForm({ amount: '', purpose: '', site: '', projectName: '', mainHead: '', subHead: '' });
      setRequestOpen(false);
      setTimeout(() => setRequestSuccess(null), 3000);
      fetchTransactions();
    } catch (err) {
      setRequestError(err.message || 'Unable to submit request');
      toast.error(err.message || 'Unable to submit request');
    }
  };

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchTransactions();
    if (['worker', 'admin'].includes(role)) fetchFormData();
  }, [dateRange.startDate, dateRange.endDate, role]);

  const filteredSubHeads = useMemo(() => {
    const selectedProjectRows = projects.filter((project) => project.project_name === requestForm.projectName);
    if (selectedProjectRows.length) {
      return [
        ...new Set(
          selectedProjectRows
            .filter((project) => !requestForm.mainHead || project.main_head_name === requestForm.mainHead)
            .map((project) => project.sub_main_name)
            .filter(Boolean)
        ),
      ].sort();
    }

    return [
      ...new Set(
        (requestForm.mainHead ? attributes.filter((attr) => attr.main_head_name === requestForm.mainHead) : attributes)
          .map((attr) => attr.sub_main_name)
          .filter(Boolean)
      ),
    ];
  }, [attributes, projects, requestForm.mainHead, requestForm.projectName]);

  const projectMainHeads = useMemo(() => {
    const selectedProjectRows = projects.filter((project) => project.project_name === requestForm.projectName);
    return selectedProjectRows.length
      ? [...new Set(selectedProjectRows.map((project) => project.main_head_name).filter(Boolean))].sort()
      : mainHeads;
  }, [mainHeads, projects, requestForm.projectName]);

  const handleProjectChange = (projectName) => {
    const selectedProjectRows = projects.filter((project) => project.project_name === projectName);
    setRequestForm((prev) => ({
      ...prev,
      projectName,
      mainHead: selectedProjectRows.length === 1 ? selectedProjectRows[0].main_head_name || '' : '',
      subHead: selectedProjectRows.length === 1 ? selectedProjectRows[0].sub_main_name || '' : '',
    }));
  };

  const projectOptions = useMemo(
    () => [...new Set(transactions.map((txn) => txn.project_name).filter(Boolean))].sort(),
    [transactions]
  );

  const stats = useMemo(() => {
    const totalCredit = transactions
      .filter((txn) => txn.amount_type === 'credit')
      .reduce((sum, txn) => sum + Number(txn.credit_amount || 0), 0);
    const totalDebit = transactions
      .filter((txn) => txn.amount_type === 'debit')
      .reduce((sum, txn) => sum + Number(txn.debit_amount || 0), 0);
    return { totalCredit, totalDebit, netBalance };
  }, [transactions, netBalance]);

  const filteredTransactions = useMemo(() => {
    let rows = [...transactions];
    if (typeFilter !== 'all') rows = rows.filter((txn) => txn.amount_type === typeFilter);
    if (projectFilter) rows = rows.filter((txn) => (txn.project_name || '') === projectFilter);
    if (search.trim()) {
      const query = search.toLowerCase();
      rows = rows.filter((txn) =>
        [
          txnNo(txn),
          txn.particuler_to,
          txn.site,
          txn.project_name,
          txn.main_head,
          txn.sub_head,
          txn.remarks,
          txn.created_by,
        ].some((value) => String(value || '').toLowerCase().includes(query))
      );
    }
    return rows;
  }, [transactions, typeFilter, projectFilter, search]);

  const exportCSV = () => {
    if (!filteredTransactions.length) {
      toast.warn('No transactions to export.');
      return;
    }
    const headers = [
      'Transaction No',
      'Date',
      'Type',
      'Credit',
      'Debit',
      'Balance',
      'Particulars',
      'Site',
      'Project',
      'Main Head',
      'Sub Head',
      'Remarks',
      'Status',
      'Created By',
      'Created At',
      'Action',
    ];
    const rows = filteredTransactions.map((txn) => [
      txnNo(txn),
      fmtDateTime(txn.transaction_date),
      txn.amount_type || '',
      txn.credit_amount || 0,
      txn.debit_amount || 0,
      txn.net_balance || 0,
      txn.particuler_to || '',
      txn.site || '',
      txn.project_name || '',
      txn.main_head || '',
      txn.sub_head || '',
      txn.remarks || '',
      txn.form_status || 'RECEIVED',
      txn.created_by || '',
      fmtDateTime(txn.created_at || txn.original_created_date || txn.created_date),
      txn.amount_type === 'credit' ? 'Return' : '',
    ]);
    const csv = [headers, ...rows]
      .map((row) =>
        row
          .map((cell) => {
            const value = String(cell ?? '');
            return value.includes(',') || value.includes('"') ? `"${value.replace(/"/g, '""')}"` : value;
          })
          .join(',')
      )
      .join('\n');
    const anchor = document.createElement('a');
    anchor.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    anchor.download = `transaction_report_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const hasActiveFilter = dateRange.startDate || dateRange.endDate || typeFilter !== 'all' || projectFilter || search;
  const showCardsView = viewMode === 'cards';
  const showTravelColumn = filteredTransactions.some(
    (txn) => isTravelTransaction(txn) || getTravelInfo(txn).from || getTravelInfo(txn).to || getTravelInfo(txn).mode
  );

  return (
    <div className="min-h-screen bg-[#f0f2f8]">
      <Navbar />
      <div className="flex pt-16">
        <Sidebar />
        <motion.main
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className={`flex-1 min-h-screen transition-all duration-300 min-w-0 ${isSidebarOpen ? 'lg:ml-72' : ''} ${
            isSidebarOpen && screenWidth < 640 ? 'opacity-50 pointer-events-none' : ''
          }`}
        >
          <div className="max-w-7xl mx-auto px-3 py-3 sm:px-5 sm:py-5 lg:px-6 lg:py-6">
            <div className="flex items-center justify-between mb-3 sm:mb-5 gap-2">
              <div>
                <h1 className="text-base sm:text-lg font-bold text-slate-800 leading-tight">My Transactions</h1>
                <p className="text-[10px] sm:text-[11px] text-slate-400 mt-0.5">
                  {filteredTransactions.length} of {transactions.length} transactions
                </p>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="flex items-center bg-white border border-slate-200 rounded-lg sm:rounded-xl p-0.5 sm:p-1 shadow-sm">
                  <button
                    onClick={() => setViewMode('table')}
                    className={`w-7 h-7 sm:w-8 sm:h-8 rounded-md sm:rounded-lg flex items-center justify-center transition-all touch-manipulation ${
                      viewMode === 'table' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-600'
                    }`}
                    aria-label="Table view"
                  >
                    <TableCellsIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('cards')}
                    className={`w-7 h-7 sm:w-8 sm:h-8 rounded-md sm:rounded-lg flex items-center justify-center transition-all touch-manipulation ${
                      viewMode === 'cards' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-600'
                    }`}
                    aria-label="Card view"
                  >
                    <Squares2X2Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                </div>
                <button
                  onClick={() => setFilterOpen((prev) => !prev)}
                  className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 h-8 sm:h-9 rounded-xl border text-xs font-semibold transition-all shadow-sm touch-manipulation ${
                    hasActiveFilter ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <FunnelIcon className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Filter</span>
                  {hasActiveFilter && <span className="w-1.5 h-1.5 rounded-full bg-white/80" />}
                </button>
                <button
                  onClick={exportCSV}
                  disabled={!filteredTransactions.length}
                  className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 h-8 sm:h-9 rounded-xl bg-white border border-slate-200 text-slate-600 text-xs font-semibold hover:border-slate-300 transition-all shadow-sm disabled:opacity-40 disabled:cursor-not-allowed touch-manipulation"
                >
                  <DocumentArrowDownIcon className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Export</span>
                </button>
                <button
                  onClick={fetchTransactions}
                  className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:text-indigo-600 transition-all shadow-sm touch-manipulation"
                >
                  <ArrowPathIcon className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            <AnimatePresence>
              {filterOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden mb-4"
                >
                  <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Filters</p>
                      {hasActiveFilter && (
                        <button
                          onClick={() => {
                            setDateRange({ startDate: '', endDate: '' });
                            setTypeFilter('all');
                            setProjectFilter('');
                            setSearch('');
                          }}
                          className="text-[11px] text-rose-500 font-semibold hover:text-rose-600"
                        >
                          Clear all
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                      <div className="sm:col-span-2 grid grid-cols-2 gap-2">
                        {[
                          { label: 'From', key: 'startDate' },
                          { label: 'To', key: 'endDate' },
                        ].map(({ label, key }) => (
                          <div key={key}>
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">{label}</label>
                            <div>
                              <input
                                type="date"
                                value={dateRange[key]}
                                onChange={(event) => setDateRange((prev) => ({ ...prev, [key]: event.target.value }))}
                                className="w-full h-9 px-3 text-[12px] font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100 transition-all"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Type</label>
                        <div className="flex gap-1 h-9 bg-slate-50 border border-slate-200 rounded-xl p-1">
                          {['all', 'credit', 'debit'].map((value) => (
                            <button
                              key={value}
                              onClick={() => setTypeFilter(value)}
                              className={`flex-1 text-[11px] font-bold rounded-lg capitalize transition-all ${
                                typeFilter === value
                                  ? value === 'credit'
                                    ? 'bg-emerald-500 text-white shadow-sm'
                                    : value === 'debit'
                                    ? 'bg-red-500 text-white shadow-sm'
                                    : 'bg-brand-600 text-white shadow-sm'
                                  : 'text-slate-500 hover:text-slate-700'
                              }`}
                            >
                              {value}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Project</label>
                        <select
                          value={projectFilter}
                          onChange={(event) => setProjectFilter(event.target.value)}
                          className="w-full h-9 px-3 text-[12px] font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100 transition-all"
                        >
                          <option value="">All Projects</option>
                          {projectOptions.map((project) => (
                            <option key={project} value={project}>{project}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="mt-3">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Search</label>
                      <div className="relative">
                        <input
                          type="text"
                          value={search}
                          onChange={(event) => setSearch(event.target.value)}
                          placeholder="Search by transaction no, party, site, head, remarks, created by..."
                          className="w-full h-9 px-3 text-[12px] font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100 transition-all"
                        />
                        {search && (
                          <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2">
                            <XMarkIcon className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4">
              <StatCard label="Total Credit" value={fmt(stats.totalCredit)} icon={ArrowTrendingUpIcon}
                bg="bg-emerald-50" iconBg="bg-emerald-500" textColor="text-emerald-700" delay={0} />
              <StatCard label="Total Debit" value={fmt(stats.totalDebit)} icon={ArrowTrendingDownIcon}
                bg="bg-red-50" iconBg="bg-red-500" textColor="text-red-700" delay={0.05} />
              <StatCard label="Net Balance" value={fmt(stats.netBalance)} icon={ScaleIcon}
                bg={stats.netBalance >= 0 ? 'bg-sky-50' : 'bg-orange-50'}
                iconBg={stats.netBalance >= 0 ? 'bg-sky-500' : 'bg-orange-500'}
                textColor={stats.netBalance >= 0 ? 'text-sky-700' : 'text-orange-700'} delay={0.1} />
            </div>

            {role === 'worker' && netBalance <= (threshold !== null ? threshold : globalThreshold) && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl px-4 py-3">
                <strong>Alert:</strong> Your net balance ({fmt(netBalance)}) is at or below the threshold limit ({fmt(threshold !== null ? threshold : globalThreshold)}).
              </div>
            )}

            {['worker', 'admin'].includes(role) && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
                className="mb-4 bg-white border border-violet-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="p-4 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-xl bg-violet-100 flex items-center justify-center flex-shrink-0">
                      <BuildingOffice2Icon className="w-5 h-5 text-violet-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[13px] font-bold text-slate-800">Request Funds</p>
                      <p className="text-[11px] text-slate-400 mt-0.5">Submit a fund request to your company owner</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setRequestOpen((prev) => !prev)}
                    className="h-10 px-5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold shadow-sm"
                  >
                    {requestOpen ? 'Close' : 'Request Funds'}
                  </button>
                </div>

                <AnimatePresence>
                  {requestOpen && (
                    <motion.form
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      onSubmit={submitRequest}
                      className="border-t border-slate-100 p-4 space-y-4"
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        <div>
                          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Amount</label>
                          <input
                            type="number"
                            value={requestForm.amount}
                            onChange={(event) => setRequestForm((prev) => ({ ...prev, amount: event.target.value }))}
                            className="w-full h-10 px-3 text-[12px] font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
                            min="0.01"
                            step="0.01"
                            placeholder="Enter amount"
                            required
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Purpose</label>
                          <input
                            type="text"
                            value={requestForm.purpose}
                            onChange={(event) => setRequestForm((prev) => ({ ...prev, purpose: event.target.value }))}
                            className="w-full h-10 px-3 text-[12px] font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
                            placeholder="Enter purpose"
                            required
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Site</label>
                          <select
                            value={requestForm.site}
                            onChange={(event) => setRequestForm((prev) => ({ ...prev, site: event.target.value }))}
                            className="w-full h-10 px-3 text-[12px] font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
                          >
                            <option value="">Select Site</option>
                            {siteOptions.map((site) => (
                              <option key={site.site_id || site.site_name} value={site.site_name}>{site.site_name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Project</label>
                          <select
                            value={requestForm.projectName}
                            onChange={(event) => handleProjectChange(event.target.value)}
                            className="w-full h-10 px-3 text-[12px] font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
                          >
                            <option value="">No Project</option>
                            {[...new Set(projects.map((project) => project.project_name).filter(Boolean))].sort().map((project) => (
                              <option key={project} value={project}>{project}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Main Head</label>
                          <select
                            value={requestForm.mainHead}
                            onChange={(event) => setRequestForm((prev) => ({ ...prev, mainHead: event.target.value, subHead: '' }))}
                            className="w-full h-10 px-3 text-[12px] font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
                          >
                            <option value="">Select Main Head</option>
                            {projectMainHeads.map((head) => (
                              <option key={head} value={head}>{head}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Sub Head</label>
                          <select
                            value={requestForm.subHead}
                            onChange={(event) => setRequestForm((prev) => ({ ...prev, subHead: event.target.value }))}
                            className="w-full h-10 px-3 text-[12px] font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
                          >
                            <option value="">Select Sub Head</option>
                            {filteredSubHeads.map((subHead) => (
                              <option key={subHead} value={subHead}>{subHead}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      {requestError && <p className="text-red-500 text-xs font-semibold">{requestError}</p>}
                      {requestSuccess && <p className="text-emerald-600 text-xs font-semibold">{requestSuccess}</p>}
                      <button type="submit" className="h-10 px-5 rounded-xl bg-brand-600 hover:bg-brand-700 text-white text-xs font-bold shadow-sm">
                        Submit Request
                      </button>
                    </motion.form>
                  )}
                </AnimatePresence>
              </motion.div>
            )}

            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl px-4 py-3">{error}</div>
            )}

            {isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} className="bg-white rounded-xl h-14 animate-pulse border border-slate-100" />
                ))}
              </div>
            ) : filteredTransactions.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-100 py-16 flex flex-col items-center gap-3 shadow-sm">
                <BanknotesIcon className="w-10 h-10 text-slate-200" />
                <p className="text-sm font-semibold text-slate-400">No transactions found</p>
                {hasActiveFilter && (
                  <button
                    onClick={() => {
                      setDateRange({ startDate: '', endDate: '' });
                      setTypeFilter('all');
                      setProjectFilter('');
                      setSearch('');
                    }}
                    className="text-xs text-brand-600 font-semibold"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              <>
                {showCardsView && (
                  <div className="space-y-2.5">
                    {filteredTransactions.map((txn, index) => (
                      <TransactionCard
                        key={`card-${txn.transaction_no || index}`}
                        txn={txn}
                        index={index}
                        role={role}
                        navigate={navigate}
                        onView={setSelectedTransaction}
                      />
                    ))}
                  </div>
                )}

                {!showCardsView && (
                  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-[12px] text-left border-collapse min-w-[1400px]">
                        <thead>
                          <tr className="border-b border-slate-100 bg-slate-50/80">
                            {[
                              'Transaction No',
                              'Date',
                              'Type',
                              'Credit',
                              'Debit',
                              'Balance',
                              'Particulars',
                              'Site',
                              'Project',
                              'Head',
                              'Sub Head',
                              'Remarks',
                              'Proof',
                              'Status',
                              'Created By',
                              'Created At',
                              ...(showTravelColumn ? ['Travel'] : []),
                              'Details',
                              ...(role === 'admin' || role === 'worker' ? ['Action'] : []),
                            ].map((heading) => (
                              <th key={heading} className="px-3 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap">
                                {heading}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                          {filteredTransactions.map((txn, index) => (
                            <motion.tr
                              key={`${txn.transaction_no || index}-${index}`}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: Math.min(index * 0.02, 0.2) }}
                              className="hover:bg-slate-50/60 transition-colors group"
                            >
                              <td className="px-3 py-2.5 text-slate-700 font-bold whitespace-nowrap">#{txnNo(txn)}</td>
                              <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap font-medium">{fmtDateTime(txn.transaction_date)}</td>
                              <td className="px-3 py-2.5"><TypeBadge type={txn.amount_type} /></td>
                              <td className="px-3 py-2.5 text-emerald-600 font-semibold">
                                {txn.amount_type === 'credit' ? fmt(txn.credit_amount) : '-'}
                              </td>
                              <td className="px-3 py-2.5 text-red-600 font-semibold">
                                {txn.amount_type === 'debit' ? fmt(txn.debit_amount) : '-'}
                              </td>
                              <td className="px-3 py-2.5 text-slate-700 font-bold">{fmt(txn.net_balance)}</td>
                              <td className="px-3 py-2.5 text-slate-700 max-w-[190px] whitespace-normal break-words leading-snug">{txn.particuler_to || '-'}</td>
                              <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{txn.site || '-'}</td>
                              <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{txn.project_name || '-'}</td>
                              <td className="px-3 py-2.5">
                                {txn.main_head ? (
                                  <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 text-[10px] font-semibold rounded-full border border-indigo-100 whitespace-nowrap">
                                    {txn.main_head}
                                  </span>
                                ) : '-'}
                              </td>
                              <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{txn.sub_head || '-'}</td>
                              <td className="px-3 py-2.5 text-slate-500 max-w-[170px] whitespace-normal break-words leading-snug">{txn.remarks || '-'}</td>
                              <td className="px-3 py-2.5"><TransactionProofImage path={txn.attachment_path} /></td>
                              <td className="px-3 py-2.5"><StatusBadge status={txn.form_status} /></td>
                              <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{txn.created_by || '-'}</td>
                              <td className="px-3 py-2.5 text-slate-400 text-[11px] whitespace-nowrap">
                                {fmtDateTime(txn.created_at || txn.original_created_date || txn.created_date)}
                              </td>
                              {showTravelColumn && (
                                <td className="px-3 py-2.5 text-slate-600 min-w-[170px]">
                                  {isTravelTransaction(txn) ? (
                                    <button type="button" onClick={() => setSelectedTransaction(txn)} className="text-left hover:text-brand-700">
                                      <p className="font-bold leading-snug break-words">{getTravelInfo(txn).from || '-'} to {getTravelInfo(txn).to || '-'}</p>
                                      <p className="text-[10px] text-slate-400 mt-0.5">{getTravelInfo(txn).mode || 'Travel'}</p>
                                    </button>
                                  ) : (
                                    <span className="text-slate-400">-</span>
                                  )}
                                </td>
                              )}
                              <td className="px-3 py-2.5">
                                <button
                                  type="button"
                                  onClick={() => setSelectedTransaction(txn)}
                                  className="px-3 py-1 bg-brand-50 border border-brand-100 text-brand-700 text-[10px] font-bold rounded-lg hover:bg-brand-100 transition-colors whitespace-nowrap"
                                >
                                  View
                                </button>
                              </td>
                              {['worker', 'admin'].includes(role) && (
                                <td className="px-3 py-2.5">
                                  {txn.amount_type === 'credit' ? (
                                    <button
                                      onClick={() => navigate(`/Return?highlight=${txn.transaction_no}`)}
                                      className="px-3 py-1 bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-bold rounded-lg hover:bg-amber-100 transition-colors whitespace-nowrap"
                                    >
                                      Return
                                    </button>
                                  ) : (
                                    <span className="text-slate-300">-</span>
                                  )}
                                </td>
                              )}
                            </motion.tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/40 flex items-center justify-between">
                      <p className="text-[11px] text-slate-400">
                        Showing <span className="font-bold text-slate-600">{filteredTransactions.length}</span> transactions
                      </p>
                      <button
                        onClick={exportCSV}
                        disabled={!filteredTransactions.length}
                        className="flex items-center gap-1.5 text-[11px] font-bold text-brand-600 hover:text-brand-700 disabled:opacity-40"
                      >
                        <DocumentArrowDownIcon className="w-3.5 h-3.5" />
                        Export CSV
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </motion.main>
      </div>
      <TransactionDetailModal txn={selectedTransaction} onClose={() => setSelectedTransaction(null)} />
    </div>
  );
};

export default WorkerTransactionReport;
